import type { StatusDisplay } from 'pages/crawler/utils/crawlerStatusUtils';

export type JobStatus = "Running" | "Scheduled" | "Done" | "Cancelled" | "Failed";
export type JobsSortBy = "lastRun" | "status" | "created";

/** A single scraped URL row within a crawler job. */
export interface JobUrl {
  id: string;
  url: string;
  depth?: number;
  status: StatusDisplay;
  cost?: number;
  crawledAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  queueRequestId?: string;
  resultKey?: string | null;
}

/** Pagination metadata returned alongside paginated URL rows. */
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  hasNextPage: boolean;
}

// Status filter for paginated request URLs within a job.
// Values match the 4 canonical statuses the server normalizes to.
export type RequestStatusFilter = "all" | "running" | "completed" | "failed" | "cancelled";
export const REQUEST_STATUS_FILTER_OPTIONS: { value: RequestStatusFilter; name: string }[] = [
  { value: "all", name: "All" },
  { value: "running", name: "Running" },
  { value: "completed", name: "Done" },
  { value: "failed", name: "Failed" },
  { value: "cancelled", name: "Cancelled" },
];

export interface CrawlerJob {
  id: string;
  status: StatusDisplay;
  startUrl: string;
  urlPattern: string;
  urlRegexpInclude: string;
  urlRegexpExclude: string | null;
  pagesScraped: number;
  pagesTarget: number;
  crawlBudget: number;
  creditsSpent: number;
  provisionalCost: number;
  createdAt: string;
  runningSince: string | null;
  finishedAt: string | null;
  supposedToRunAt: string | null;
  timeoutAt: string | null;
  crawlerName: string | null;
  crawlerStartUrl: string | null;
  crawlerApiParams: any;
  crawlerAdditionalData: any;
  apiParams: any;
  history: any;
  additionalData: any;
  webhookUrl: string | null;
  maxDepth: number;
  delay: number;
  runBy: string | null;
}

/** A crawler item from the /users/crawlers list endpoint. */
export interface CrawlerListItem {
  id: string;
  name: string;
  domain: string;
  jobs: number;
  creditsSpent: number;
  status: StatusDisplay;
  hasRunningJobs: boolean;
  enabled: boolean;
  lastFinishedAt: string | null;
  lastStartedAt: string | null;
  supposedToRunAt: string | null;
  createdAt: string;
}

/** Summary of a single crawler from the GET /users/crawlers/:id endpoint. */
export interface CrawlerSummary {
  id: string;
  name: string;
  createdAt: string;
  status: StatusDisplay;
  apiParams: any;
  traits: number;
}

export interface CrawlerDetails {
  id: string;
  name: string;
  status: string;
  created: string;
  avgCostPerJob: number;
  totalCreditsSpent: number;
  outputFormat: string;
  autoparse?: boolean;
  deviceType: string;
  retry404: boolean;
  totalJobs: number;
}
